import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Phone,
  User,
  Users,
  Video,
  MessageSquare,
  Star,
} from 'lucide-react';
import { fetchInterviews } from '@/lib/api';
import type { Interview, InterviewAttendee } from '@/types';
import { formatDateTime } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

interface InterviewListProps {
  candidateId?: string;
  jobId?: string;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  scheduled: {
    label: 'Scheduled',
    className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-500/20 text-green-400 border-green-500/30',
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-red-500/20 text-red-400 border-red-500/30',
  },
  no_show: {
    label: 'No Show',
    className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  },
};

const TYPE_CONFIG: Record<string, { label: string; icon: typeof Phone }> = {
  phone: { label: 'Phone', icon: Phone },
  video: { label: 'Video', icon: Video },
  onsite: { label: 'On-site', icon: MapPin },
  panel: { label: 'Panel', icon: Users },
};

export function InterviewList({ candidateId, jobId }: InterviewListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['interviews', { candidateId, jobId }],
    queryFn: () => fetchInterviews({ candidateId, jobId, limit: 50 }),
  });

  const interviews = data?.data ?? [];

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 bg-bg-tertiary rounded-lg border border-white/10">
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-lg bg-white/5" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48 bg-white/5" />
                <Skeleton className="h-3 w-32 bg-white/5" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full bg-white/5" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (interviews.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-12 w-12 mx-auto text-text-tertiary mb-4" />
        <p className="text-text-secondary font-medium">No interviews scheduled</p>
        <p className="text-sm text-text-tertiary mt-1">
          Schedule an interview to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {interviews.map((interview) => (
        <InterviewCard
          key={interview.id}
          interview={interview}
          isExpanded={expandedId === interview.id}
          onToggle={() => toggleExpand(interview.id)}
        />
      ))}
    </div>
  );
}

interface InterviewCardProps {
  interview: Interview;
  isExpanded: boolean;
  onToggle: () => void;
}

function InterviewCard({ interview, isExpanded, onToggle }: InterviewCardProps) {
  const statusConfig = STATUS_CONFIG[interview.status] ?? {
    label: interview.status,
    className: 'bg-white/10 text-text-secondary border-white/20',
  };

  const typeConfig = TYPE_CONFIG[interview.type] ?? {
    label: interview.type,
    icon: Calendar,
  };

  const TypeIcon = typeConfig.icon;

  const scheduledDate = new Date(interview.scheduledAt);
  const isPast = scheduledDate < new Date();

  return (
    <div className="bg-bg-tertiary rounded-lg border border-white/10 overflow-hidden transition-colors hover:border-white/20">
      {/* Main row */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-inset rounded-lg"
      >
        <div className="flex items-center gap-4">
          {/* Type icon */}
          <div className="h-10 w-10 rounded-lg bg-bg-elevated flex items-center justify-center flex-shrink-0">
            <TypeIcon className="h-5 w-5 text-emerald-400" />
          </div>

          {/* Title and date */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-text-primary truncate">
                {interview.title}
              </p>
              <Badge className={`text-xs border ${typeConfig.label === interview.type ? '' : ''}${statusConfig.className}`}>
                {statusConfig.label}
              </Badge>
            </div>
            <div className="flex items-center gap-3 mt-1 text-sm text-text-tertiary">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDateTime(interview.scheduledAt)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {interview.durationMinutes} min
              </span>
              <Badge
                variant="outline"
                className="text-xs py-0 px-1.5 border-white/10"
              >
                {typeConfig.label}
              </Badge>
            </div>
          </div>

          {/* Attendee avatars */}
          <div className="flex items-center">
            {interview.attendees.length > 0 && (
              <div className="flex -space-x-2 mr-3">
                {interview.attendees.slice(0, 3).map((attendee: InterviewAttendee) => (
                  <div
                    key={attendee.id}
                    className="h-7 w-7 rounded-full bg-bg-elevated border-2 border-bg-tertiary flex items-center justify-center"
                    title={
                      attendee.user
                        ? `${attendee.user.firstName} ${attendee.user.lastName}`
                        : attendee.externalName || 'Attendee'
                    }
                  >
                    <User className="h-3.5 w-3.5 text-text-tertiary" />
                  </div>
                ))}
                {interview.attendees.length > 3 && (
                  <div className="h-7 w-7 rounded-full bg-bg-elevated border-2 border-bg-tertiary flex items-center justify-center text-xs text-text-tertiary">
                    +{interview.attendees.length - 3}
                  </div>
                )}
              </div>
            )}
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-text-tertiary" />
            ) : (
              <ChevronDown className="h-4 w-4 text-text-tertiary" />
            )}
          </div>
        </div>
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
          {/* Candidate and Job info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1">
                Candidate
              </p>
              <p className="text-sm text-text-primary">
                {interview.candidate.firstName} {interview.candidate.lastName}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1">
                Job
              </p>
              <p className="text-sm text-text-primary">{interview.job.title}</p>
            </div>
          </div>

          {/* Location or Meeting Link */}
          {interview.location && (
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1">
                Location
              </p>
              <p className="text-sm text-text-primary flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-text-tertiary" />
                {interview.location}
              </p>
            </div>
          )}

          {interview.meetingLink && (
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1">
                Meeting Link
              </p>
              <a
                href={interview.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-emerald-400 hover:underline flex items-center gap-1.5"
              >
                <Video className="h-3.5 w-3.5" />
                {interview.meetingLink}
              </a>
            </div>
          )}

          {/* Attendees */}
          {interview.attendees.length > 0 && (
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                Attendees ({interview.attendees.length})
              </p>
              <div className="space-y-1.5">
                {interview.attendees.map((attendee: InterviewAttendee) => (
                  <div
                    key={attendee.id}
                    className="flex items-center gap-2 text-sm"
                  >
                    <div className="h-6 w-6 rounded-full bg-bg-elevated flex items-center justify-center">
                      <User className="h-3 w-3 text-text-tertiary" />
                    </div>
                    <span className="text-text-primary">
                      {attendee.user
                        ? `${attendee.user.firstName} ${attendee.user.lastName}`
                        : attendee.externalName}
                    </span>
                    {attendee.externalEmail && (
                      <span className="text-text-tertiary">
                        ({attendee.externalEmail})
                      </span>
                    )}
                    <Badge
                      variant="outline"
                      className="text-xs py-0 px-1.5 ml-auto border-white/10"
                    >
                      {attendee.rsvpStatus}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {interview.notes && (
            <div>
              <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-1">
                Notes
              </p>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">
                {interview.notes}
              </p>
            </div>
          )}

          {/* Feedback (if completed) */}
          {interview.feedback && (
            <div className="p-3 bg-bg-elevated rounded-md border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-4 w-4 text-emerald-400" />
                <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider">
                  Feedback
                </p>
                {interview.rating && (
                  <div className="flex items-center gap-0.5 ml-auto">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-3.5 w-3.5 ${
                          star <= interview.rating!
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-text-tertiary'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">
                {interview.feedback}
              </p>
            </div>
          )}

          {/* Time indicator */}
          {isPast && interview.status === 'scheduled' && (
            <div className="flex items-center gap-2 p-2 bg-yellow-500/10 border border-yellow-500/20 rounded-md text-sm text-yellow-400">
              <Clock className="h-3.5 w-3.5" />
              This interview is past its scheduled time and may need to be updated.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
